import { useEntity } from "apps/console/context/entity";
import ConsoleApi from "apps/console/enums/consoleApi";
import { QueryClient, useQueryClient } from "react-query";
import { useCopies } from "shared/hooks/useCopies";
import { ApiEventParticipant } from "shared/types/ApiEventParticipant";
import { ApiEventParticipantAddTagRequest } from "shared/types/ApiEventParticipantAddTagRequest";
import { errorToast, successToast } from "shared/utils/toastNotification";
import { ApiRegistrationOrganization } from "../../registrationOrganization/details/types";
import { UploadImageProps } from "../../../../components/profileImage/types";
import { updateApprovalsList } from "../list/dataloader";
import {
  ApiRegistrationParticipantForAdminResponse,
  ApiRegistrationRequest,
  ApiRateCardForApplicationFlow,
  UseFindRegistrationParticipantByIdForAdmin
} from "./types";
import { useOutletContext } from "react-router-dom";
import { ParticipantListContext } from "../list/types";
import { getApiQueryKey, useGetApiData, useGetApiDataAsMutate, usePostApiData } from "shared/datalayer";
import { ApiRegistrationParticipantForListForAdminResponse } from "../list/types";
import { AnyObject } from "global";

export function getQueryKeyForFindRegistrationParticipantByIdForAdmin(
  input: UseFindRegistrationParticipantByIdForAdmin
) {
  return getApiQueryKey(ConsoleApi.FIND_REGISTRATION_PARTICIPANT_BY_ID, {
    params: { registrationParticipantId: input.registrationParticipantId, registrationFormId: input.registrationFormId }
  });
}

export function useFindRegistrationParticipantByIdForAdmin({
  registrationParticipantId,
  registrationFormId,
  enabled = true
}: UseFindRegistrationParticipantByIdForAdmin) {
  return useGetApiData<ApiRegistrationParticipantForAdminResponse>(
    ConsoleApi.FIND_REGISTRATION_PARTICIPANT_BY_ID,
    { params: { registrationParticipantId, registrationFormId } },
    { enabled: Boolean(registrationParticipantId) && enabled }
  );
}

export function useFindRegistrationParticipantByIdAndOrgId({
  registrationParticipantId,
  organizationId,
  enabled,
  regFormId,
  onSuccessHandler
}: {
  registrationParticipantId: string;
  enabled: boolean;
  organizationId: string;
  onSuccessHandler: Function;
  regFormId?: string | null;
}) {
  return useGetApiData<ApiRegistrationParticipantForAdminResponse>(
    ConsoleApi.FORM_FOR_PARTICIPANT_AND_ORGANIZATION,
    { params: { registrationParticipantId, organizationId, registrationFormId: regFormId } },
    {
      enabled,
      onSuccess: res => onSuccessHandler(res),
      queryKey: [ConsoleApi.FORM_FOR_PARTICIPANT_AND_ORGANIZATION, registrationParticipantId, organizationId]
    }
  );
}

export function useUpdateRegistrationProfileByAdmin() {
  const copies = useCopies();
  return usePostApiData<ApiRegistrationParticipantForAdminResponse, ApiRegistrationRequest>(
    ConsoleApi.UPDATE_REGISTRAION_PROFILE,
    {
      onSuccess: response => {
        if (response.message) {
          errorToast(response.message);
        } else {
          successToast(copies.get("console.participant.profile.update.success.msg"));
        }
      }
    }
  );
}

export function usePerformAdminRegistrationAction() {
  const copies = useCopies();
  return usePostApiData<ApiRegistrationParticipantForAdminResponse, ApiRegistrationRequest>(
    ConsoleApi.UPDATE_REGISTRAION_STATUS,
    { onSuccess: () => successToast(copies.get("changes.saved")) }
  );
}

export function useFindForApplicationFlow({ registrationId }: { registrationId: string }) {
  return useGetApiData<ApiRateCardForApplicationFlow[]>(
    ConsoleApi.RATE_CARD_FOR_APPROVAL_FLOW,
    { params: { registrationId } },
    { enabled: Boolean(registrationId) }
  );
}

export function useGetApprovalsListDetailById() {
  const queryClient = useQueryClient();
  const { registrationId, registrationDetails } = useEntity();
  const context = useOutletContext<ParticipantListContext>();

  return useGetApiDataAsMutate<ApiRegistrationParticipantForListForAdminResponse>(
    ConsoleApi.APPROVAL_LIST_DETAIL_BY_ID,
    {
      onSuccess: (data: ApiRegistrationParticipantForListForAdminResponse) => {
        if (registrationDetails?.paginationEnabled) {
          context?.updatePaginatedList?.(data);
        } else {
          updateApprovalsList({ data, queryClient, registrationId });
        }
      }
    }
  );
}

export function useResendConfirmationNumber() {
  const copies = useCopies();
  return useGetApiDataAsMutate<void>(ConsoleApi.RESEND_CONFIRMATION_NUMBER_EMAIL, {
    onSuccess: () => successToast(copies.get("email.sent"))
  });
}

export function useResendResumeRegistration() {
  const copies = useCopies();
  return useGetApiDataAsMutate<void>(ConsoleApi.RESEND_RESUME_REGISTRATION_EMAIL, {
    onSuccess: () => successToast(copies.get("email.sent"))
  });
}

export function useResendInvoicePayerResumeRegistration(invoicePaymentRequestId: string) {
  const copies = useCopies();
  return usePostApiData<void, AnyObject>(ConsoleApi.RESEND_RESUME_REGISTRATION_EMAIL_TO_INVOICE_PAYER, {
    clientOptions: { params: { invoicePaymentRequestId } },
    onSuccess: () => successToast(copies.get("email.sent"))
  });
}

export function useGetListSponsoringOrganizationsForParticipant({
  registrationParticipantId,
  registrationFormId,
  successHandler
}: {
  registrationParticipantId?: string;
  registrationFormId?: string;
  successHandler?: (data: Array<ApiRegistrationOrganization>) => void;
}) {
  return useGetApiData<Array<ApiRegistrationOrganization>>(
    ConsoleApi.GET_SPONSORING_ORGANIZATION_FOR_PARTICIPANT,
    { params: { registrationParticipantId, registrationFormId } },
    {
      onSuccess: data => successHandler?.(data),
      enabled: Boolean(registrationFormId) && Boolean(registrationParticipantId)
    }
  );
}

export function useAddEventParticipantTag() {
  const copies = useCopies();
  return usePostApiData<ApiEventParticipant, ApiEventParticipantAddTagRequest>(ConsoleApi.ADD_EVENT_PARTICIPANT_TAG, {
    onSuccess: data =>
      successToast(
        copies.get(
          data.tags?.length
            ? "participant.add.as.agenda.participant.success.msg"
            : "participant.remove.as.agenda.participant.success.msg"
        )
      )
  });
}

export function updateRegisteredParticipantAfterImageUpload({
  queryClient,
  registrationFormId,
  uploadResponse
}: {
  queryClient: QueryClient;
  registrationFormId: string | null;
  uploadResponse: UploadImageProps;
}) {
  queryClient.setQueryData(
    getQueryKeyForFindRegistrationParticipantByIdForAdmin({
      registrationParticipantId: uploadResponse.id,
      registrationFormId
    }),
    (data: any) => {
      return {
        ...data,
        profilePictureLocation: uploadResponse.profilePictureLocation,
        uiVersion: uploadResponse.uiVersion
      };
    }
  );
}

export function useAnonymiseRegistrationParticipant(id: string) {
  return usePostApiData<ApiRegistrationParticipantForAdminResponse, any>(
    ConsoleApi.ANONYMISE_REGISTRATION_PARTICIPANT,
    { clientOptions: { params: { registrationParticipantId: id } } }
  );
}

export function useSyncParticipantToIntegration() {
  const copies = useCopies();
  return useGetApiDataAsMutate(ConsoleApi.REGISTRATION_PARTICIPANT_SYNC_TO_INTEGRATION, {
    onSuccess: () => successToast(copies.get("sync.participant.to.integration.success.msg"))
  });
}

export function useSendBadgeEmail() {
  const copies = useCopies();
  return useGetApiDataAsMutate(ConsoleApi.REGISTRATION_PARTICIPANT_BADGE_EMAIL_SEND, {
    onSuccess: () => successToast(copies.get("send.badge.email.success.msg"))
  });
}
